import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CardContainer } from './MemberDirectorySettings'
import StyledText from '../../components/StyledComponents/StyledText'
import DataTable from '../../components/listing/DataTable'
// import { Column, TableConfig } from '../../types/types'
import { DropResult } from '@hello-pangea/dnd'
import { Table } from '../../components/Table'
import { ActionColumn, Column } from '../../types/table'
import { CorporateTheme, font } from '../../utils/themes'
import LocationIcon from '../../assets/icons/LocationIcon'
import MobileIcon from '../../assets/icons/MobileIcon'
import DesktopIcon from '../../assets/icons/DesktopIcon'
import { formatCustomDate } from '../../utils/helper'
import { useGetLoginHistoryQuery } from '../../api/accountApiSlice'
import { authFetch } from '../../utils/authFetch'

interface LoginHistory {
    id: number;
    device: string;
    // location: string;
    ipAddress: string;
    type: string;
    lastActive: string;
}

// const login_data: LoginHistory[] = [
//     {
//         id: 1,
//         device: "Chrome On MacOS",
//         location: "New York, USA",
//         ipAddress: "203.0.113.255",
//         lastActive: "2026-04-01T18:45:30.123Z"
//     },
//     {
//         id: 2,
//         device: "IPHONE 15 Pro Max",
//         location: "San Francisco, USA",
//         ipAddress: "203.0.113.255",
//         lastActive: "2026-04-01T18:45:30.123Z"
//     },
//     {
//         id: 3,
//         device: "Safari On Ipad Pro",
//         location: "Los Angeles, USA",
//         ipAddress: "203.0.113.255",
//         lastActive: "2026-04-01T18:45:30.123Z"
//     },
//     {
//         id: 4,
//         device: "Chrome On Window 11",
//         location: "Chicago, USA",
//         ipAddress: "203.0.113.255",
//         lastActive: "2026-04-01T18:45:30.123Z"
//     },
//     {
//         id: 5,
//         device: "Chrome On Android",
//         location: "Austin, USA",
//         ipAddress: "203.0.113.255",
//         lastActive: "2026-04-01T18:45:30.123Z"
//     }
// ];


const checkDeviceType = (userAgent: string | null | undefined): 'desktop' | 'mobile' | 'unknown' => {
  // ✅ Guard clause: handle null/undefined/empty strings
  if (!userAgent) return 'unknown';
  
  const lowerUA = userAgent.toLowerCase();
  
  if (/windows|mac/.test(lowerUA)) return 'desktop';
  if (/android|iphone|ipad/.test(lowerUA)) return 'mobile';
  
  return 'unknown';
};

const LoginHistory = () => {

    const [loginData, setLoginData] = useState<LoginHistory[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // const { data: loginHistoryData, error, isLoading, isFetching } = useGetLoginHistoryQuery({ page: 1 });

    useEffect(() => {
        let cancelled = false; // ✅ Proper cleanup flag

        const fetchLoginHistory = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const apiBase = process.env.REACT_APP_API_URL;
                if (!apiBase) {
                    throw new Error('API URL not configured');
                }

                // ✅ Fixed: Removed extra '}' in URL
                const response = await authFetch(`${apiBase}/portal/me/login-history`);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const json = await response.json();

                if (!cancelled) {
                    // ✅ Fixed: Your API returns { events: [...] }, not a plain array
                    const events = json?.events || [];
                    const transformed = events.map((event: any) => ({
                        id: event.id,
                        device: `${event?.deviceInfo?.client || ''}${event?.deviceInfo?.client && event?.deviceInfo?.os ? ' / ' : ''}${event?.deviceInfo?.os || ''}` || '-',
                        // device: event.userAgent,
                        ipAddress: event.ipAddress,
                        type: event.eventType,
                        lastActive: event.createdAt
                    }));
                    setLoginData(transformed);
                }
            } catch (err: any) {
                if (!cancelled) {
                    console.error('Failed to fetch login history:', err);
                    setError(err?.message || 'Failed to load login history');
                    setLoginData([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void fetchLoginHistory();

        // ✅ Cleanup function to prevent state updates on unmounted component
        return () => {
            cancelled = true;
        };
    }, []);

    const columns: Array<Column<LoginHistory> | ActionColumn<LoginHistory>> = [
        {
            key: 'device',
            title: 'Device',
            width: '250px',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '20px' }}>
                    {
                        checkDeviceType(record.device) === 'mobile' ? (
                            <MobileIcon />
                        ) : (
                            <DesktopIcon />
                        )
                    }
                    </div>
                    <div>
                        <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.headerColor}>{record.device}</StyledText>
                    </div>
                </div>
            )
        },
        {
            key: 'ipAddress',
            title: 'IP Address',
            width: '250px',
            align: 'center',
            render: (_, record) => (
                <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.headerColor}>{record.ipAddress}</StyledText>
            )
        },
        {
            key: 'type',
            title: 'Type',
            width: '250px',
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    {/* <LocationIcon /> */}
                    {/* <div> */}
                        <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.headerColor}>{record.type}</StyledText>
                    {/* </div> */}
                </div>
            )
        },
        {
            key: 'lastActive',
            title: 'Last Active',
            width: '250px',
            align: 'center',
            render: (_, record) => (
                <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.headerColor}>{formatCustomDate(record.lastActive)}</StyledText>
            )
        }

    ];

  return (
    <CardContainer>
        <div className='border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between'>
            <StyledText
                fontSize='15px'
                fontWeight={700}
                textColor={'var(--app-primary-color)'}
            >
                Login History
            </StyledText>
            <StyledText
                fontSize='13px'
                fontWeight={600}
                textColor='rgba(255, 109, 65, 1)'
            >
                Terminate all login sessions
            </StyledText>
        </div>
        <div>
            

            <Table<LoginHistory>
                columns={columns}
                data={loginData}
                showActions={false}
                onRowClick={(record) => console.log('Row clicked:', record)}
                emptyMessage="No login history found"
                loading={isLoading}
            />
            
        </div>
    </CardContainer>
  )
}

export default LoginHistory